/**
 * i18n setup (M-06). Supports English (default) and Arabic.
 *
 * Language choice is persisted in localStorage under `cutmate:lang`
 * and reflected on the <html> element with `lang` and `dir`
 * attributes so RTL layouts (M-08) work out of the box.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import ar from "./locales/ar.json";

export const SUPPORTED_LANGS = ["en", "ar"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

const RTL_LANGS = new Set<SupportedLang>(["ar"]);
const STORAGE_KEY = "cutmate:lang";

function detectInitialLang(): SupportedLang {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "ar") return stored;
  // Browser language fallback
  const nav = window.navigator.language?.slice(0, 2).toLowerCase();
  return nav === "ar" ? "ar" : "en";
}

export function applyDocumentDirection(lang: SupportedLang): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
}

export async function setLanguage(lang: SupportedLang): Promise<void> {
  await i18n.changeLanguage(lang);
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage can fail in private browsing; ignore
  }
  applyDocumentDirection(lang);
}

const initialLang = detectInitialLang();

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: initialLang,
  fallbackLng: "en",
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyDocumentDirection(initialLang);

export default i18n;
